import axios from "axios";

const portalApi = axios.create({ baseURL: window.location.origin });

// Sticky lock flag. The backend license gate covers /api/v1/portal/* too, so a
// hard-locked appliance 402s every portal request. We latch the lock here so
// PortalLicenseGate can show the unavailable screen immediately on mount —
// before any of that page's own requests have had a chance to fire (or fail).
let _licenseLocked = false;
export const isPortalLicenseLocked = () => _licenseLocked;
// Released by PortalLicenseGate's recovery poll once the appliance stops 402ing.
export const clearPortalLicenseLock = () => {
  _licenseLocked = false;
};

portalApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("portal_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

portalApi.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    if (status === 401) {
      localStorage.removeItem("portal_token");
      if (
        window.location.pathname.startsWith("/portal") &&
        window.location.pathname !== "/portal/login"
      ) {
        window.location.href = "/portal/login";
      }
    } else if (status === 402) {
      // Appliance license enforcement (same backend gate as the tech UI). The
      // portal is customer-facing, so unlike the admin client we never echo the
      // raw server detail or use "license" language — customers can't renew the
      // MSP's license; they can only wait or contact their provider.
      const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
      if (detail === "license_locked") {
        // Hard lock (expired past grace / revoked / trial ended) — replace the
        // portal with the unavailable screen.
        _licenseLocked = true;
        window.dispatchEvent(new CustomEvent("portal-license:locked"));
      } else {
        // Grace-period read-only (license_read_only) or any other 402 — the
        // page still works, this one action was blocked. Surface a calm notice.
        window.dispatchEvent(
          new CustomEvent("portal-license:notice", {
            detail: {
              message:
                "This action isn't available right now. Please try again shortly, or contact your IT provider.",
            },
          }),
        );
      }
    }
    return Promise.reject(err);
  },
);

/** Authenticated portal download for an endpoint that takes a request BODY.
 *
 * A plain `<a href>` sends no Authorization header, so it lands on the login
 * page or a bare `{"detail":"Not authenticated"}` — which is exactly the bug
 * reported against the compliance framework download. Fetching as a blob with
 * the interceptor attached is the only way these work.
 *
 * Error bodies arrive as a Blob too, so `err.response.data.detail` is
 * undefined unless we read it back; without that every failed download reports
 * a generic message instead of the reason the server gave.
 */
export async function portalDownload(url: string, fallbackName: string, body?: unknown) {
  let r;
  try {
    // GET when there is no body. The tabular reports POST their column/filter
    // spec; a generated document is identified entirely by its URL, and
    // POSTing an empty object to fetch one reads as a mistake.
    r =
      body === undefined
        ? await portalApi.get(url, { responseType: "blob" })
        : await portalApi.post(url, body, { responseType: "blob" });
  } catch (err: any) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text().catch(() => "");
      try {
        err.response.data = JSON.parse(text);
      } catch {
        err.response.data = { detail: text };
      }
    }
    throw err;
  }
  const cd = (r.headers["content-disposition"] || "") as string;
  const fn = /filename="([^"]+)"/.exec(cd)?.[1] || fallbackName;
  const blob = new Blob([r.data], {
    type: (r.headers["content-type"] as string) || "application/octet-stream",
  });
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  a.download = fn;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 500);
}

export default portalApi;
